/* eslint-disable max-len */
/* eslint-disable react/prop-types */
/* eslint-disable object-curly-newline */
/* eslint-disable import/no-extraneous-dependencies */
import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Modal, Box } from '@mui/material';
import { Search, RefreshCw } from 'lucide-react';
import moment from 'moment-timezone';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import { NotificationContext } from '../../contexts/Notification';
import './activityLogs.css';
import { Navbar, Sidebar } from '../../components';
import DataTable from '../../components/DataTable';
import StatusPill from '../../components/StatusPill';
import useDebounce from '../../hooks/useDebounce';
import DateRangePicker from '../../components/dateRangePicker';
import TimezoneDropdown from '../../components/selectors/TimezoneDropdown';
import { STYLED_SELECT_CLASS, STYLED_SELECT_STYLE } from '../../utils/styledSelect';

const EVENT_TYPE_OPTIONS = [
  { value: '', label: 'All Event Types' },
  { value: 'signin', label: 'Sign In' },
  { value: 'signout', label: 'Sign Out' },
  { value: 'view', label: 'View' },
  { value: 'export', label: 'Export' },
  { value: 'filter', label: 'Filter' },
  { value: 'post', label: 'Create' },
  { value: 'put', label: 'Update' },
  { value: 'delete', label: 'Delete' },
];

const MODULE_OPTIONS = [
  { value: '', label: 'All Modules' },
  { value: 'Authentication', label: 'Authentication' },
  { value: 'Timesheets', label: 'Timesheets' },
  { value: 'Screenshots', label: 'Screenshots' },
  { value: 'API', label: 'API' },
];

// Role → StatusPill tone (uses existing tones only).
const ROLE_TONE = {
  'Primary owner': 'info',
  'Workspace owner': 'info',
  'Workspace admin': 'info',
  'Team Leader': 'neutral',
  'Team member': 'neutral',
};

const dash = <span className="text-ink-500">—</span>;

// --- Activity-details formatting (raw JSON -> readable key/value rows) ---
const fmtDate = (v) => moment(v).format('MMM DD, YYYY');
const humanizeKey = (key) => key
  .replace(/[_-]+/g, ' ')
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/\b\w/g, (c) => c.toUpperCase())
  .trim();
const formatDetailValue = (key, val) => {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'object') {
    if (val.start && val.end) return `${fmtDate(val.start)} – ${fmtDate(val.end)}`;
    if (Array.isArray(val)) return val.length ? val.join(', ') : '—';
    return JSON.stringify(val);
  }
  if (/url/i.test(key) && typeof val === 'string') return val.split('/').pop();
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(val)) return moment(val).format('MMM DD, YYYY HH:mm');
  if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return fmtDate(val);
  return String(val);
};
const buildDetailRows = (details) => {
  if (!details || typeof details !== 'object') return [];
  const d = { ...details };
  const rows = [];
  if (d.targetMemberName || d.targetMemberEmail) {
    rows.push({ label: 'Target Member', value: [d.targetMemberName, d.targetMemberEmail].filter(Boolean).join(' · ') });
    delete d.targetMemberName;
    delete d.targetMemberEmail;
    delete d.targetMemberId;
  }
  Object.entries(d).forEach(([key, val]) => {
    if (/id$/i.test(key)) return; // hide internal ids
    rows.push({ label: humanizeKey(key), value: formatDetailValue(key, val) });
  });
  return rows;
};

function DetailRow({ label, value }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start gap-0.5 sm:gap-4">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-500 sm:w-40 flex-shrink-0" style={{ letterSpacing: '0.05em', paddingTop: 1 }}>
        {label}
      </span>
      <span className="text-sm text-ink-900 break-words min-w-0">{value}</span>
    </div>
  );
}

function ActivityLogs() {
  const history = useHistory();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const page = query.get('page');

  const { activeWorkspace, user } = useContext(UserContext);
  const { displayNotification } = useContext(NotificationContext);

  const [activityLogs, setActivityLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(page ? parseInt(page, 10) : 1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCounts, setTotalCounts] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { debounce } = useDebounce();
  const [sortKey, setSortKey] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedTimezone, setSelectedTimezone] = useState(activeWorkspace?.timezone || 'UTC');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [dateRange, setDateRange] = useState(() => {
    const today = moment.tz(selectedTimezone).toDate();
    const weekAgo = moment.tz(selectedTimezone).subtract(7, 'days').toDate();
    return [weekAgo, today];
  });

  const handleViewDetails = (row) => {
    setSelectedRow(row);
    setShowDetailsModal(true);
  };

  const fetchActivityLogs = useCallback(async () => {
    setIsLoading(true);
    const [startDate, endDate] = dateRange;

    const startOfDay = moment.tz(startDate, selectedTimezone).startOf('day').clone().utc()
      .format('YYYY-MM-DD HH:mm:ss');
    const endOfDay = moment.tz(endDate, selectedTimezone).endOf('day').clone().utc()
      .format('YYYY-MM-DD HH:mm:ss');

    try {
      const { success, data } = await sendRequest({
        method: 'get',
        url: API.MEMBER.activityLogs(),
        params: {
          page: currentPage,
          pageSize,
          search: searchQuery,
          eventType: eventTypeFilter,
          module: moduleFilter,
          startDate: startOfDay,
          endDate: endOfDay,
          sortKey,
          sortOrder,
        },
        timeout: 30000,
      });

      if (success) {
        const mappedLogs = data.activityLogs.map((log, idx) => {
          const logUser = log.member?.user;
          const userEmail = logUser?.email || log.userEmail || 'Unknown User';
          const userName = [logUser?.firstName, logUser?.lastName].filter(Boolean).join(' ').trim();
          const userRole = log.member?.accountType || 'Unknown Role';

          let details = {};
          try {
            if (log.newValue) {
              details = typeof log.newValue === 'string' ? JSON.parse(log.newValue) : log.newValue;
            }
          } catch (error) {
            details = {};
          }

          let enhancedDescription = log.description || '-';
          if (log.eventType === 'view' && log.module === 'Screenshots' && details.targetMemberName) {
            enhancedDescription = `Viewed screenshot of ${details.targetMemberName}`;
            if (details.screenshotUrl) {
              const fileName = details.screenshotUrl.split('/').pop();
              enhancedDescription += ` (${fileName})`;
            }
          } else if (log.eventType === 'view' && log.module === 'Activity' && details.targetMemberName) {
            enhancedDescription = `Viewed activity details of ${details.targetMemberName}`;
            if (details.dateRange && details.dateRange.start && details.dateRange.end) {
              try {
                enhancedDescription += ` (${moment(details.dateRange.start).format('MMM DD')} - ${moment(details.dateRange.end).format('MMM DD')})`;
              } catch (error) { /* ignore */ }
            }
          } else if (log.eventType === 'view' && log.module === 'Timesheets') {
            if (details.dateRange && details.dateRange.start && details.dateRange.end) {
              try {
                enhancedDescription = `Viewed timesheets (${moment(details.dateRange.start).format('MMM DD')} - ${moment(details.dateRange.end).format('MMM DD')})`;
              } catch (error) {
                enhancedDescription = 'Viewed timesheets';
              }
            } else {
              enhancedDescription = 'Viewed timesheets';
            }
          } else if (log.eventType === 'export' && log.module === 'Timesheets' && details.memberCount) {
            enhancedDescription = `Exported timesheets data (${details.memberCount} members)`;
          }
          if (typeof enhancedDescription !== 'string') enhancedDescription = '-';

          return {
            id: log.id ?? `${log.date}-${idx}`,
            date: log.date,
            userEmail,
            userName,
            userRole,
            event: log.event,
            eventType: log.eventType || '-',
            module: log.module,
            description: enhancedDescription,
            details,
            hasDetails: Object.keys(details).length > 0,
          };
        });

        setActivityLogs(mappedLogs);
        setTotalCounts(data.totalCount || mappedLogs.length);
      } else {
        displayNotification({ message: 'Failed to fetch activity logs. Please try again.', severity: 'error' });
      }
    } catch (error) {
      displayNotification({ message: 'An error occurred while fetching activity logs.', severity: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage, pageSize, searchQuery, eventTypeFilter, moduleFilter,
    dateRange, selectedTimezone, sortKey, sortOrder, displayNotification,
  ]);

  const handlePageChange = (next) => {
    setCurrentPage(next);
    history.push(`/activity-logs?page=${next}`);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
    history.push('/activity-logs?page=1');
  };

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  const handleSelectTimezone = (timezone) => {
    setSelectedTimezone(timezone);
    setDateRange([
      moment.tz(timezone).subtract(7, 'days').toDate(),
      moment.tz(timezone).toDate(),
    ]);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedRow(null);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearchDebounced = useCallback(debounce((value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  }, 300), []);

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
    handleSearchDebounced(e.target.value);
  };

  useEffect(() => {
    fetchActivityLogs();
  }, [
    currentPage, pageSize, searchQuery, eventTypeFilter, moduleFilter,
    dateRange, selectedTimezone, sortKey, sortOrder, fetchActivityLogs,
  ]);

  const columns = [
    {
      key: 'date',
      label: 'Date & Time',
      sortable: true,
      width: 180,
      render: (r) => <span className="text-ink-900 whitespace-nowrap tabular-nums">{moment(r.date).format('MMM DD, YYYY HH:mm:ss')}</span>,
    },
    {
      key: 'user',
      label: 'User',
      sortable: true,
      width: 220,
      flex: true,
      render: (r) => (
        <div className="min-w-0">
          {r.userName ? (
            <>
              <span className="block font-medium text-ink-900 truncate" title={r.userName}>{r.userName}</span>
              <span className="block text-xs text-ink-500 truncate" title={r.userEmail}>{r.userEmail}</span>
            </>
          ) : (
            <span className="block text-ink-900 truncate" title={r.userEmail}>{r.userEmail}</span>
          )}
        </div>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      width: 140,
      render: (r) => <StatusPill tone={ROLE_TONE[r.userRole] || 'neutral'}>{r.userRole}</StatusPill>,
    },
    {
      key: 'event',
      label: 'Event',
      sortable: true,
      width: 160,
      flex: true,
      render: (r) => <span className="text-ink-900 truncate block">{r.event || dash}</span>,
    },
    {
      key: 'eventType',
      label: 'Event Type',
      sortable: true,
      width: 120,
      render: (r) => <span className="text-ink-700">{r.eventType}</span>,
    },
    {
      key: 'module',
      label: 'Module',
      sortable: true,
      width: 120,
      render: (r) => <span className="text-ink-700">{r.module || dash}</span>,
    },
    {
      key: 'description',
      label: 'Description',
      width: 240,
      flex: true,
      render: (r) => <span className="text-ink-700 truncate block" title={r.description}>{r.description}</span>,
    },
    {
      key: 'details',
      label: 'Details',
      width: 110,
      align: 'center',
      render: (r) => (
        <button
          type="button"
          onClick={() => handleViewDetails(r)}
          data-testid="view-details"
          className="inline-flex items-center justify-center px-3 py-1.5 rounded-[8px] text-[11px] font-semibold uppercase tracking-wider text-ink-900 bg-white border border-rule hover:border-brand-500 hover:text-brand-700 transition"
          style={{ borderStyle: 'solid', letterSpacing: '0.05em' }}
        >
          View
        </button>
      ),
    },
  ];

  const detailRows = buildDetailRows(selectedRow?.details);
  const contextRows = selectedRow ? [
    { label: 'User', value: selectedRow.userName ? `${selectedRow.userName} · ${selectedRow.userEmail}` : selectedRow.userEmail },
    { label: 'Role', value: selectedRow.userRole },
    { label: 'Date & Time', value: moment(selectedRow.date).format('MMM DD, YYYY HH:mm:ss') },
    { label: 'Event', value: selectedRow.event || '—' },
    { label: 'Event Type', value: selectedRow.eventType || '—' },
    { label: 'Module', value: selectedRow.module || '—' },
    { label: 'Description', value: selectedRow.description || '—' },
  ] : [];

  const tzOptions = [
    ...(user?.timezone ? [{ heading: 'Your timezone', description: user.timezone, value: user.timezone }] : []),
    ...(activeWorkspace?.timezone && activeWorkspace.timezone !== user?.timezone
      ? [{ heading: activeWorkspace.name || 'Workspace timezone', description: activeWorkspace.timezone, value: activeWorkspace.timezone }]
      : []),
  ];

  return (
    <div>
      <Navbar />
      <Sidebar />
      <div className="screen-wrapper">
        <div className="screen-header" style={{ fontFamily: 'Inter, sans-serif' }}>
          <div className="flex flex-col gap-4 mb-5">
            <div className="min-w-0">
              <h1 className="font-bold text-ink-900 leading-tight truncate text-2xl lg:text-[32px] lg:leading-10" data-testid="title" data-tour="activity-logs">
                Activity Logs
              </h1>
              <p className="text-sm text-ink-500 mt-1" data-testid="subtitle">
                Monitor user activities and system events
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <div className="relative w-full sm:w-[320px]" style={{ height: 40 }}>
                <Search size={14} className="text-ink-500 pointer-events-none" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 1 }} />
                <input
                  type="text"
                  value={searchInput}
                  onChange={handleSearchChange}
                  placeholder="Search users, events, or descriptions"
                  data-testid="search"
                  className="w-full rounded-[8px] bg-white text-ink-900 placeholder:text-ink-500 border border-rule focus:outline-none focus:border-brand-500"
                  style={{ height: 40, paddingLeft: 36, paddingRight: 12, fontSize: 14, lineHeight: '20px', borderStyle: 'solid', boxSizing: 'border-box' }}
                />
              </div>
              <select
                value={eventTypeFilter}
                onChange={(e) => { setEventTypeFilter(e.target.value); setCurrentPage(1); }}
                className={`${STYLED_SELECT_CLASS} w-full sm:w-auto min-w-[150px]`}
                style={STYLED_SELECT_STYLE}
              >
                {EVENT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select
                value={moduleFilter}
                onChange={(e) => { setModuleFilter(e.target.value); setCurrentPage(1); }}
                className={`${STYLED_SELECT_CLASS} w-full sm:w-auto min-w-[140px]`}
                style={STYLED_SELECT_STYLE}
              >
                {MODULE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <div className="w-full sm:w-[240px]">
                <DateRangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  className="w-full"
                  placement="bottomEnd"
                />
              </div>
              <TimezoneDropdown
                value={selectedTimezone}
                onChange={handleSelectTimezone}
                options={tzOptions}
              />
              <button
                type="button"
                onClick={fetchActivityLogs}
                disabled={isLoading}
                className="inline-flex w-full items-center justify-center gap-1.5 px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 disabled:opacity-50 transition sm:w-auto"
                style={{ borderStyle: 'solid' }}
              >
                <RefreshCw size={16} />
                Refresh
              </button>
            </div>
          </div>

          <DataTable
            columns={columns}
            rows={activityLogs}
            getRowKey={(r) => r.id}
            sortKey={sortKey}
            sortDir={sortOrder}
            onSort={toggleSort}
            loading={isLoading}
            emptyText="No activity logs found."
            page={currentPage}
            pageSize={pageSize}
            total={totalCounts}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            minWidth={1280}
          />
        </div>
      </div>

      {/* Details modal */}
      {showDetailsModal && (
        <Modal open onClose={handleCloseDetailsModal} className="flex-row align-c jstfy-c" data-testid="activity-details-modal">
          <Box style={{
            width: 640,
            maxWidth: 'calc(100% - 32px)',
            maxHeight: '85vh',
            overflowY: 'auto',
            padding: 24,
            background: '#fff',
            borderRadius: 12,
          }}
          >
            <h5 className="text-lg font-semibold leading-none text-ink-900 mb-4">Activity Details</h5>
            <div
              style={{
                background: '#fbfbfc',
                border: '1px solid #f1f2f5',
                padding: 16,
                borderRadius: 8,
              }}
            >
              <div className="flex flex-col gap-3">
                {contextRows.map(({ label, value }) => <DetailRow key={label} label={label} value={value} />)}
              </div>
              {detailRows.length > 0 && (
                <>
                  <div style={{ borderTop: '1px solid #e8eaf0', margin: '16px 0' }} />
                  <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink-500 mb-3" style={{ letterSpacing: '0.05em' }}>
                    Additional Details
                  </span>
                  <div className="flex flex-col gap-3">
                    {detailRows.map(({ label, value }) => <DetailRow key={label} label={label} value={value} />)}
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-start items-center gap-2 mt-6">
              <button
                type="button"
                onClick={handleCloseDetailsModal}
                className="inline-flex items-center justify-center px-4 py-2 border border-rule rounded-[8px] text-sm font-medium text-ink-900 bg-white hover:bg-gray-50 transition"
                style={{ borderStyle: 'solid' }}
              >
                Close
              </button>
            </div>
          </Box>
        </Modal>
      )}
    </div>
  );
}

export default ActivityLogs;
